import React, { useCallback, useEffect, useMemo, useState } from "react";

import { ECode } from "~/_enums/ECode";
import { getUserAccounts } from "~/api";
import { postCommonUser } from "~/api-versioned/User/postCommonUser";
import { env } from "~/env";
import { DesktopCommunityJoin } from "~/modules/Router/Auth/components/DesktopCommunityJoin";
import { DesktopCommunityJoinModal } from "~/modules/Router/Auth/components/DesktopCommunityJoinModal";
import { DesktopWalletConnect } from "~/modules/Router/Auth/components/DesktopWalletConnect";
import { DaoMemberInfoType, ErrorTypes } from "~/modules/Router/Auth/types";
import { disconnect } from "~/modules/Router/Auth/utils/auth/disconnect";
import { getDaoInfo } from "~/modules/Router/Auth/utils/join/getDaoInfo";
import { getIsJoinedDao } from "~/modules/Router/Auth/utils/join/getIsJoinedDao";
import { useAppDispatch } from "~/store";
import { setGlobalMode } from "~/store/globalSlice";
import { setMarketplaceVisibility } from "~/store/market/slice";
import { setMerchstoreUrl } from "~/store/merchSlice";
import { Error as ErrorPage } from "../../../../router/routes";
import { AuthDispatch } from "../contexts/AuthDispatchContext";
import { AuthState, UserAccounts } from "../contexts/AuthStateContext";
import { usePathRef } from "../hooks";
import { Initializing } from "./index";
import { MainModule } from "./MainModule";
import { TDao } from "../../../../_types/TDao";

function AuthGuard({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch();

  const {
    decodedPath2Ref,
    subdomainRef,
    isLoginPath,
    isAuthExcludedPath,
    isNotFoundPath,
    isErrorUrlRef,
  } = usePathRef();

  const [isAuthLoading, setAuthLoading] = useState(true);
  const [isLogin, setIsLogin] = useState(false);
  const [dao, setDao] = useState<TDao | null>(null);
  const [daoMemberInfo, setDaoMemberInfo] = useState<{
    isJoined: boolean;
    daoMemberInfo: DaoMemberInfoType | null;
  } | null>(null);

  const [authState, setAuthState] = useState<AuthState>({
    userId: ``,
    address: ``,
    daoId: ``,
    memberId: ``,
    isEmailAccount: false,
    categoryId: "",
    accounts: null,
    needWalletConnect: false,
    subdomain: ``,
  });
  const isOk = useMemo(
    () => isLogin && dao && daoMemberInfo,
    [isLogin, dao, daoMemberInfo],
  );
  const [openCommunityJoinModal, setOpenCommunityJoinModal] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleCloseCommunityJoinModal = () => {
    setOpenCommunityJoinModal(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleOpenCommunityJoinModal = () => {
    setOpenCommunityJoinModal(true);
  };

  const handleDisconnect = useCallback(() => {
    disconnect().then((result) => {
      if (result.ok) {
        location.replace("/login");
      }
    });
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateAuthState = async () => {
    const result = await postCommonUser();

    if (result.code === ECode.Success && result.data.id) {
      const value = result.data;
      setIsLogin(true);

      let userAccounts: UserAccounts = null;
      let needWalletConnect = false;
      try {
        const userAccountsResponse = await getUserAccounts({
          userId: value.id,
          emailAccount: value.emailAccounts.length ? "true" : "false",
        });
        if (userAccountsResponse.code === "000") {
          userAccounts = userAccountsResponse.data;
          needWalletConnect =
            userAccountsResponse.data.evmCompatibleAccounts.length === 0;
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e);
      } finally {
        const address =
          value.evmCompatibleAccounts.at(0)?.address ??
          value.emailAccounts.at(0)?.address ??
          value.googleAccounts.at(0)?.address ??
          "";

        setAuthState((prev) => ({
          ...prev,
          userId: value.id,
          address,
          isEmailAccount: Boolean(value.emailAccounts.length),
          accounts: userAccounts,
          needWalletConnect, // accounts 정보 요청 실패시 false 가정 처리
        }));
      }
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateDaoMemberInfo = ({ daoId }: { daoId: string }) => {
    return getIsJoinedDao({ daoId }).then((result) => {
      if (result.ok) {
        setDaoMemberInfo(result.value);
        setAuthState((prev) => ({
          ...prev,
          memberId: result.value.daoMemberInfo?.id ?? "",
        }));
      } else {
        setDaoMemberInfo({
          isJoined: false,
          daoMemberInfo: null,
        });
      }
    });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateDaoId = (daoId: string) => {
    setAuthState((prev) => ({
      ...prev,
      daoId,
    }));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateMemberId = (memberId: string) => {
    setAuthState((prev) => ({
      ...prev,
      memberId,
    }));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateCategoryId = (categoryId: string) => {
    setAuthState((prev) => ({
      ...prev,
      categoryId,
    }));
  };

  const updateSubdomain = () => {
    const [, subdomain] = /^\/(.*)(?:\/mobile)/g.exec(location.pathname) ?? [];

    if (subdomain) {
      setAuthState((prev) => ({ ...prev, subdomain }));
    }
  };

  const authDispatch = useMemo<AuthDispatch>(
    () => ({
      handleDisconnect,
      handleOpenCommunityJoinModal,
      handleCloseCommunityJoinModal,
      updateAuthState,
      updateDaoMemberInfo,
      updateDaoId,
      updateMemberId,
      updateCategoryId,
    }),
    [
      handleDisconnect,
      handleOpenCommunityJoinModal,
      handleCloseCommunityJoinModal,
      updateAuthState,
      updateDaoMemberInfo,
      updateDaoId,
      updateMemberId,
      updateCategoryId,
    ],
  );

  useEffect(() => {
    updateSubdomain();
    if (isErrorUrlRef.current) {
      setAuthLoading(false);
      return;
    }

    let getDaoInfoPromise = Promise.resolve();
    if (subdomainRef.current) {
      getDaoInfoPromise = getDaoInfo({ subdomain: subdomainRef.current }).then(
        (result) => {
          if (result.ok) {
            const daoId = result.value.id;
            const categoryId = result.value.categoryId ?? "0";
            const subdomain = result.value.subdomain;
            setDao(result.value);
            setAuthState((prev) => ({
              ...prev,
              daoId,
              categoryId,
              subdomain,
            }));

            dispatch(setMerchstoreUrl(result.value?.merchstoreUrl));
            dispatch(
              setMarketplaceVisibility(result.value?.marketplaceVisibility),
            );
            dispatch(setGlobalMode(result.value?.mode));
          } else {
            if (result.value === ErrorTypes.NotExistingDaoError) {
              location.href = "/error/404";
            }
          }
        },
      );
    }
    Promise.all([getDaoInfoPromise, updateAuthState()]).finally(() => {
      setAuthLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (authState.daoId && authState.userId) {
      updateDaoMemberInfo({ daoId: authState.daoId });
    }
  }, [authState.daoId, authState.userId]);

  // 해당 라인 아래부터 수동 라우팅 부분입니다.
  if (isLoginPath) {
    location.href = env.ROOT_REDIRECT_URL;
    return null;
  }

  if (isAuthExcludedPath) {
    return isAuthLoading ? null : (
      <MainModule
        dao={dao}
        subdomain={dao?.subdomain ?? null}
        authState={authState}
        authDispatch={authDispatch}
      >
        {openCommunityJoinModal && (
          <DesktopCommunityJoinModal
            isOpen={openCommunityJoinModal}
            onClose={handleCloseCommunityJoinModal}
            isEmailAccount={authState.isEmailAccount}
            userId={authState.userId}
          />
        )}
        {children}
      </MainModule>
    );
  }

  if (isErrorUrlRef.current) {
    return (
      <ErrorPage code={isNotFoundPath ? "404" : decodedPath2Ref.current} />
    );
  }

  if (isAuthLoading) {
    return <Initializing />;
  }

  // 로그아웃 상태일때
  if (!isLogin) {
    return (
      <MainModule
        dao={dao}
        subdomain={dao?.subdomain ?? null}
        authState={authState}
        authDispatch={authDispatch}
      >
        <DesktopWalletConnect />
      </MainModule>
    );
  }

  // 커뮤니티 가입 유무 처리
  if (daoMemberInfo) {
    if (daoMemberInfo.isJoined) {
      return isOk ? (
        <MainModule
          dao={dao}
          subdomain={dao?.subdomain ?? null}
          authState={authState}
          authDispatch={authDispatch}
        >
          {openCommunityJoinModal && (
            <DesktopCommunityJoinModal
              isOpen={openCommunityJoinModal}
              onClose={handleCloseCommunityJoinModal}
              isEmailAccount={authState.isEmailAccount}
              userId={authState.userId}
            />
          )}
          {children}
        </MainModule>
      ) : null;
    } else {
      return (
        <MainModule
          dao={dao}
          subdomain={dao?.subdomain ?? null}
          authState={authState}
          authDispatch={authDispatch}
        >
          <DesktopCommunityJoin
            {...{
              isEmailAccount: authState.isEmailAccount,
              userId: authState.userId,
            }}
          />
        </MainModule>
      );
    }
  }
  return null;
}

export default AuthGuard;
